import { DataAction } from '../../../enum/data-action.enum';
import { ExampleData } from '../../models/example-data.model';
import { takeUntil, Subject, switchMap, Observable, map, of, lastValueFrom } from 'rxjs';
import { Option } from '../../../models/option.model';
import { Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { ExampleService } from '../../services/example.service';
import { Component } from '@angular/core';
import { ExampleMaster } from '../../models/example-master.model';
import { SimpleSingleComponent } from '../../../components/abstract/simple/simple-single.component';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-example-single',
  templateUrl: './example-single.component.html',
  styleUrls: ['./example-single.component.scss']
})
export class ExampleSingleComponent extends SimpleSingleComponent {

  override componentClassName: string = 'ExampleSingleComponent';

  override masterData: ExampleMaster | undefined;

  // 下拉選單的 Options 資料
  exampleOptions: Option[] = [];

  // 動態下拉選單的 Options 資料
  dynamicOptions: Option[] = [];

  // 自動完成的 Suggestions 資料
  exampleDataSuggestions: ExampleData[] = [];
  // 自動完成欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private exampleDataSubject$ = new Subject<void>();

  // 自動完成 + 下拉的 Suggestions 資料
  allExampleData?: ExampleData[];
  // 自動完成 + 下拉的 Suggestions 資料
  allExampleDataSuggestions: ExampleData[] = [];
  // 自動完成 + 下拉欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private allExampleDataSubject$ = new Subject<void>();

  constructor(private exampleService: ExampleService) {
    super();
  }

  override initFormGroup(): void {
    this.formGroup = this.formBuilder.nonNullable.group({
      id: '',
      uuid: '',
      input: ['', Validators.required],
      calendar: ['', Validators.required],
      dropdown: '',
      autoComplete: '',
      inputNumber: '',
      calendarRange: '',
      custValidation: ['', this.custValidator()],
      dynamicDropdown: '',
      autoCompleteDropdown: ''
    });
  }

  override initFormControlDisabled(): void {
    // 如果單頭表單欄位沒有特別需要控制不可編輯就不用寫
    return;
  }

  override initStaticOptions(): void {
    // 下拉選單選項
    this.exampleOptions = [
      {
        id: 'option.yes',
        label: this.translateService.instant('option.yes'),
        value: 'Y'
      },
      {
        id: 'option.no',
        label: this.translateService.instant('option.no'),
        value: 'N'
      }
    ];
  }

  override async initFormData(): Promise<any> {
    // 抓取 Master Data
    this.masterData = await lastValueFrom(this.loadMasterData());

    this.formGroup.patchValue({
      id: this.masterData?.id,
      uuid: this.masterData?.uuid,
      input: this.masterData?.input,
      calendar: this.masterData?.calendar,
      dropdown: this.masterData?.dropdown,
      inputNumber: this.masterData?.inputNumber,
      custValidation: this.masterData?.custValidation,
      dynamicDropdown: this.masterData?.dynamicDropdown
    });

    console.log('setMasterData = ' + JSON.stringify(this.formGroup.getRawValue()));

    await this.setDynamicOptions();
    await Promise.all([this.patchAutoComplete(), this.patchAutoCompleteDropdown()]);
  }

  /**
   * 這裡應該是去取 uuid 重新查資料，或是直接拿前面傳入的資料來顯示，要自己判斷處理。
   * 為了統一處理的行為，建議都重新取資料比較一致，且資料會是最新的資料
   *
   * @returns
   */
  loadMasterData(): Observable<ExampleMaster> {
    console.log('ExampleSingleComponent loadMasterData isDialogMode = ' + this.isDialogMode);

    // 為了統一處理的行為，建議都重新取資料比較一致，且資料會是最新的資料
    if (this.isDialogMode) {

      // 以下取 masterDataKeyValue 在 2023/08/13 取消，改在 BaseFormComponent 的 ngOnInit 就先取回。

      // 取出查詢 Master Data 的 Key
      // const data: ExampleMaster = this.dynamicDialogConfig.data;
      // const dataAction: DataAction = this.dynamicDialogConfig.data?.dataAction;

      // console.log('ExampleSingleComponent loadMasterData dynamicDialogConfig = ' + JSON.stringify(this.dynamicDialogConfig));
      // console.log('ExampleSingleComponent loadMasterData dataAction = ' + dataAction);

      // this.masterDataKeyValue = this.dynamicDialogConfig.data[this.masterDataKeyName];
    } else {
      this.masterDataKeyValue = this.activatedRoute.snapshot.paramMap.get(this.masterDataKeyName);
    }

    console.log('ExampleSingleComponent loadMasterData uuid = ' + this.masterDataKeyValue);

    // 如果前面是做新增，而且沒有傳 key 的值，就產生一個空的
    if (this.parentDataAction == DataAction.NEW && !this.masterDataKeyValue) {
      let exampleMaster = new ExampleMaster();
      exampleMaster.id = DataAction.NEW;
      exampleMaster.uuid = uuidv4();
      return of(exampleMaster);
    } else {
      return this.exampleService.getExampleMasterData(this.masterDataKeyValue!).pipe(
        takeUntil(this._destroying$)
      );
    }
  }

  /**
   * 建議這些要在程式一開始前就取回來的東西分不同 function 處理
   */
  async setDynamicOptions(): Promise<void> {
    console.log('setDynamicOptions start');
    this.dynamicOptions = await lastValueFrom(
      this.exampleService.queryAllExampleDataOptions().pipe(takeUntil(this._destroying$))
    );
    console.log('setDynamicOptions end');
  }

  async patchAutoComplete(): Promise<void> {
    console.log('patchAutoComplete start');
    const keyword = this.masterData?.autoCompleteCode;
    if (keyword) {
      const data: ExampleData[] = await lastValueFrom(
        this.exampleService.queryExampleDatas(keyword).pipe(takeUntil(this._destroying$))
      );

      if (data.length > 0) {
        this.formGroup.patchValue({
          autoComplete: data.find(exampleData => exampleData.exampleDataCode == keyword)
        });
      } else {
        throw new Error('AutoComplete data ' + keyword + ' not found');
      }
    }
  }

  async patchAutoCompleteDropdown(): Promise<void> {
    console.log('patchAutoCompleteDropdown start');
    const keyword = this.masterData?.autoCompleteDropdownCode;
    if (keyword) {
      const data: ExampleData[] = await lastValueFrom(
        this.exampleService.queryExampleDatas(keyword).pipe(takeUntil(this._destroying$))
      );

      if (data.length > 0) {
        this.formGroup.patchValue({
          autoCompleteDropdown: data.find(exampleData => exampleData.exampleDataCode == keyword)
        });
      } else {
        throw new Error('AutoCompleteDropdown data ' + keyword + ' not found');
      }
    }
  }

  override afterFormDataLoadSuccess(): void {
    return;
  }

  override afterFormDataLoadFail(): void {
    return;
  }

  override initExtraMasterMenu(): void {
    this.masterMenu.push(
      {
        id: this.parseToId('btnMasterMenuOther'),
        label: '其他功能按鈕',
        automationId: '其他功能按鈕',
        command: () => {
          // 這裡應該去呼叫獨立寫的一個 function 做事情
          this.systemMessageService.info('按下其他功能按鈕');
        }
      },
      {
        id: this.parseToId('btnMasterMenuNoIcon'),
        label: '如果不能給有意義的 icon 就只放文字就好',
        command: () => {
          // 這裡應該去呼叫獨立寫的一個 function 做事情
          this.systemMessageService.info('按下如果不能給有意義的 icon 就只放文字就好');
        }
      }
    );
  }

  override setExtraMasterMenuStatus(): void {
    console.log('setExtraMasterMenuStatus');
    let masterMenuButtonOther = this.masterMenu.find(menuItem => menuItem.id == this.parseToId('btnMasterMenuOther'));
    if (masterMenuButtonOther) {
      masterMenuButtonOther.disabled = !this.masterInViewStatus();
    }

    let masterMenuButtonNoIcon = this.masterMenu.find(menuItem => menuItem.id == this.parseToId('btnMasterMenuNoIcon'));
    if (masterMenuButtonNoIcon) {
      masterMenuButtonNoIcon.disabled = !this.masterInViewStatus();
    }
  }

  override afterLangChange(): void {
    this.languageService.processOptionTranslation(this.exampleOptions);
  }

  override afterMasterMenuStatusChange(): void {
    return;
  }

  override async beforeMasterMenuDiscard(): Promise<boolean> {
    return true;
  }

  override async beforeMasterMenuSubmit(): Promise<boolean> {
    return true;
  }

  override async doMasterMenuSubmit(): Promise<boolean> {
    return new Promise(resolve => {
      // 實作存檔時要呼叫 API 存入資料，成功後記得要呼叫傳入的 callback function
      // setTimeout 是故意模擬處理的時間，實際上不會這樣做
      setTimeout(() => {

        let submitData: ExampleMaster = new ExampleMaster();
        // 存檔後如果 id 是 new 表示新增的資料，把 id 清空，之後才不會被判斷成是新增未存檔的資料
        submitData.id = this.formGroup.getRawValue().id != DataAction.NEW ? this.formGroup.getRawValue().id : '';
        submitData.uuid = this.formGroup.getRawValue().uuid;
        submitData.input = this.formGroup.getRawValue().input;
        submitData.calendar = this.formGroup.getRawValue().calendar;
        submitData.dropdown = this.formGroup.getRawValue().dropdown;
        submitData.autoCompleteCode = this.formGroup.getRawValue().autoComplete?.exampleDataCode;
        submitData.autoCompleteName = this.formGroup.getRawValue().autoComplete?.exampleDataName;
        submitData.autoCompleteNameTw = this.formGroup.getRawValue().autoComplete?.exampleDataNameTw;
        submitData.autoCompleteNameCn = this.formGroup.getRawValue().autoComplete?.exampleDataNameCn;
        submitData.autoCompleteNameUs = this.formGroup.getRawValue().autoComplete?.exampleDataNameUs;
        submitData.inputNumber = this.formGroup.getRawValue().inputNumber;
        submitData.calendarRange = this.formGroup.getRawValue().calendarRange;
        submitData.custValidation = this.formGroup.getRawValue().custValidation;
        submitData.dynamicDropdown = this.formGroup.getRawValue().dynamicDropdown;
        submitData.autoCompleteDropdownCode = this.formGroup.getRawValue().autoCompleteDropdown?.exampleDataCode;
        submitData.autoCompleteDropdownName = this.formGroup.getRawValue().autoCompleteDropdown?.exampleDataName;
        submitData.autoCompleteDropdownNameTw = this.formGroup.getRawValue().autoCompleteDropdown?.exampleDataNameTw;
        submitData.autoCompleteDropdownNameCn = this.formGroup.getRawValue().autoCompleteDropdown?.exampleDataNameCn;
        submitData.autoCompleteDropdownNameUs = this.formGroup.getRawValue().autoCompleteDropdown?.exampleDataNameUs;

        console.log('submitData = ' + JSON.stringify(submitData));

        this.masterData = submitData;

        // 儲存資料的時候要把資料也更新回 masterData
        // 注意的是 id 值欄位，要在呼叫後端後也回傳這些值才不會有問題
        // 因為新增資料 id 會是空的，如果沒有回寫，前端又回來修改這筆資料就會出錯
        // 下面是模擬有回傳，實際上不會這樣做
        // if (this.parentDataAction == DataAction.NEW) {
        //   this.masterData.id = uuidv4();
        // }

        resolve(true);
      }, this.exampleService.delay);
    });
  }

  override async doMasterMenuDelete(): Promise<boolean> {
    return new Promise(resolve => {
      // TODO 實作刪除時要呼叫 API 刪除資料，成功後記得要呼叫傳入的 callback function
      // setTimeout 是故意模擬處理的時間，實際上不會這樣做
      setTimeout(() => {
        this.systemMessageService.info('模擬實際刪除會花一點時間處理');
        resolve(true);
      }, this.exampleService.delay);
    });
  }

  custValidator(): ValidatorFn {
    return (formControl: AbstractControl): ValidationErrors | null => {
      if (!formControl.parent) {
        return null;
      }

      // 欄位要做什麼驗證
      const error = formControl.parent?.get('custValidation')?.value == '1';

      // 如果有錯
      if (error) {
        // 回傳 ValidationErrors，duplicate 是 error 的 key
        return { duplicate: true };
      }
      // 驗證結果符合需求，回傳 null
      return null;
    }
  }

  searchExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.exampleDataSubject$.observed) {
      this.exampleDataSubject$.pipe(
        switchMap(keyword => this.exampleService.queryExampleDatas(keyword!)),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.exampleDataSuggestions = data;
        console.log('this.exampleDataSuggestions = ' + this.exampleDataSuggestions.length);
      });
    }

    this.exampleDataSubject$.next(event.query);
  }

  searchAllExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.allExampleDataSubject$.observed) {
      this.allExampleDataSubject$.pipe(
        switchMap(keyword => {
          // 如果 allExampleData 已經有取過值了就用它，不然就呼叫 API 取
          let data: Observable<ExampleData[]> = this.allExampleData ? of(this.allExampleData) : this.exampleService.queryAllExampleDatas()
          return data.pipe(
            map(data => {
              // 把回傳所有的值塞到 allExampleData
              this.allExampleData = data;
              // 根據輸入的關鍵字過濾
              return data.filter(exampleData => exampleData.exampleDataName.indexOf(keyword!) >= 0)
            })
          );
        }),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.allExampleDataSuggestions = data;
      });
    }

    this.allExampleDataSubject$.next(event.query);
  }
}
